/**
 * usePublishedClipSource.ts
 *
 * Resolves what the clip preview's "Published Version" mode should play for the
 * active aspect ratio, and whether that AR has a published render at all.
 *
 * Only the canonical cell — original language × AR × highest resolution —
 * mirrors its render to the Video record's `videoFilePrimary`, so a clip
 * published solely in a translation has that field empty and every published-
 * state check in the modal reads it as never published (PLAT-11548). The
 * translated render exists only as a `PublishRecord` pointing at an S3 output,
 * which is what the Downloads tab already lists — this hook reads the same
 * records (`PublishRecordsStore` fetches once per videoId and dedupes, so the
 * second consumer costs no extra request) and signs the chosen one's URL.
 *
 * The original language keeps its existing `videoFilePrimary` → Mux path
 * untouched: that render is already Mux-processed, so it stays on adaptive
 * streaming while a translated render plays as a progressive CloudFront MP4.
 */

import {
  newestTranslatedRender,
  selectPinnedRender,
  selectTranslatedRender,
} from '@src/services/publish/publishedRenderSelection';
import { requestPublishRecordResources } from '@src/services/VideoResourcesProvider';
import { isOriginalLang } from '@src/utility/captionLanguage';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useClipPublishRecords } from './useClipPublishRecords';

import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';
import type { PublishRecord } from '@src/types.ts/publishRecord';
import type { StoryVideo } from '@src/types.ts/story';

const logger = LogManager.getLogger('hook:usePublishedClipSource');

type Resolved = { recordId: string; src: string } | null;

/** Re-use the previous state when it already names this render, so a record
 *  arriving under a new identity each render cannot loop the resolve effect. */
function keepIfSame(prev: Resolved, recordId: string, src: string): Resolved {
  return prev?.recordId === recordId && prev.src === src
    ? prev
    : { recordId, src };
}

/** A playable published render — one of `playbackId` (Mux) or `src` (CloudFront). */
export interface PublishedClipSource {
  /** Mux playback id, for the canonical render the Video record already holds. */
  playbackId?: string;
  /** Direct MP4 URL, for a translated render read from its publish record. */
  src?: string;
  /** Measured length of the render, when the record reports one. */
  durationSeconds?: number;
  /**
   * The language whose render is playing, when it came from a publish record.
   * `displayLanguage` decides whether it is worth naming.
   */
  language?: string;
}

interface Params {
  /** The active variant's Video record id (per aspect ratio). */
  videoId: string | undefined;
  adapter: Partial<IVideoCreatorStore> | null;
  /** Transcript language selection; `null` is the story's original language. */
  language: string | null;
  /** The active variant's legacy mirrored render, when it has one. */
  videoFilePrimary: StoryVideo | undefined;
  /** The story's own language — the one that needs no naming. */
  originalLanguage: string | undefined;
  /**
   * A publish picked out of the Downloads list, which overrides the automatic
   * choice — including an older one for the same language.
   */
  pinnedRecordId: string | null;
}

interface Result {
  /** What to play, or `null` when nothing published can be resolved. */
  source: PublishedClipSource | null;
  /** True while records are loading or a signed URL is being resolved. */
  loading: boolean;
  /**
   * Whether this aspect ratio has any published render — the replacement for
   * the `videoFilePrimary` published-state check.
   */
  hasPublishedRender: boolean;
  /**
   * The language to name beside "Published Version", or `null` when there is
   * nothing worth saying — the render is the story's own language, so the
   * label would be noise. Set whenever a translated render is playing, which is
   * what keeps the player from silently disagreeing with the transcript
   * language dropdown: pick Original on a clip published only in Ukrainian and
   * the label says Ukrainian, because that is what you are hearing.
   */
  displayLanguage: string | null;
  /**
   * The record whose bytes are playing, or `null` when it is the mirrored
   * render. Lets the header state *which* publish is on screen — language,
   * format, resolution and time — rather than the clip's most recent publish
   * across variants, which can be a different format entirely.
   */
  sourceRecord: PublishRecord | null;
}

export function usePublishedClipSource({
  videoId,
  adapter,
  language,
  videoFilePrimary,
  originalLanguage,
  pinnedRecordId,
}: Params): Result {
  const { records, loading: recordsLoading } = useClipPublishRecords(
    videoId,
    adapter,
  );

  // The canonical render, when the Video record holds one. Its playback needs
  // no record lookup and no signing.
  const canonicalSource = useMemo<PublishedClipSource | null>(() => {
    if (!videoFilePrimary) return null;
    return {
      playbackId: videoFilePrimary.video?.muxPlaybackId,
      // PLAT-10009 — a PublishRecord-synthesized `videoFilePrimary` points at
      // the S3 output and has no Mux asset, so the file URL stands in.
      src: videoFilePrimary.video?.muxPlaybackId
        ? undefined
        : videoFilePrimary.url,
      durationSeconds: videoFilePrimary.video?.duration,
    };
  }, [videoFilePrimary]);

  /**
   * The record to play, or `null` to use the canonical render. An explicit pick
   * from the Downloads list wins outright — it is the user naming the exact
   * publish, older ones included. Failing that, the selected language's own
   * render; failing that, the newest render of any language, so a clip
   * published only in Ukrainian plays that — there is nothing else it could
   * mean by "the published version". `displayLanguage` then labels it.
   */
  const record = useMemo<PublishRecord | null>(() => {
    const pinned = selectPinnedRender(records, pinnedRecordId);
    if (pinned) return pinned;
    const forLanguage = isOriginalLang(language)
      ? null
      : selectTranslatedRender(records, language);
    if (forLanguage) return forLanguage;
    // The canonical render answers for the original language; only reach for
    // another language's render when there is no canonical one to play.
    if (canonicalSource) return null;
    return newestTranslatedRender(records);
  }, [records, language, canonicalSource, pinnedRecordId]);

  const [resolved, setResolved] = useState<Resolved>(null);
  const [resolving, setResolving] = useState(false);
  // Signed URLs are stable for the session, so a language flipped back and
  // forth re-reads the cache instead of re-signing.
  const signedByRecordId = useRef(new Map<string, string>());

  useEffect(() => {
    // Every path below that starts no fetch has nothing to wait for, and has to
    // lower `resolving` itself: a selection change abandons any fetch already
    // running (`cancelled`), so that fetch's `finally` no longer speaks for the
    // hook and would otherwise leave the flag — and the modal's spinner — stuck
    // on for the rest of the session.
    if (!record) {
      setResolving(false);
      return;
    }
    // A synthesized legacy record stores the direct download URL in `fileKey`
    // (there is no real record to sign against) — same branch
    // `usePublishRecordDownload` takes.
    if (record.id.startsWith('legacy-')) {
      const legacyUrl = record.outputJson?.video?.fileKey;
      // `useClipPublishRecords` mints the synthetic legacy record in its render
      // body, so this effect re-fires on every render with an identical record
      // under a new identity — committing a fresh object here would loop.
      if (legacyUrl)
        setResolved(prev => keepIfSame(prev, record.id, legacyUrl));
      setResolving(false);
      return;
    }
    const cached = signedByRecordId.current.get(record.id);
    if (cached) {
      setResolved(prev => keepIfSame(prev, record.id, cached));
      setResolving(false);
      return;
    }

    let cancelled = false;
    setResolving(true);
    void (async () => {
      try {
        const resources = await requestPublishRecordResources(record.id, [
          'video',
        ]);
        const url = resources.video?.url;
        if (!url) {
          logger.warn('No signed video URL for publish record', {
            recordId: record.id,
            language: record.language,
          });
          return;
        }
        signedByRecordId.current.set(record.id, url);
        if (!cancelled) setResolved({ recordId: record.id, src: url });
      } catch (error) {
        // A published render we can't sign degrades to the canonical path (or
        // to no source at all) rather than breaking the preview.
        logger.error('Failed to resolve published render URL', {
          recordId: record.id,
          ...extractErrorInfo(error),
        });
      } finally {
        if (!cancelled) setResolving(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [record]);

  const source = useMemo<PublishedClipSource | null>(() => {
    if (record) {
      if (resolved?.recordId !== record.id) return null;
      const output = record.outputJson?.video;
      return {
        src: resolved.src,
        durationSeconds: output?.durationMs
          ? output.durationMs / 1000
          : undefined,
        language: record.language,
      };
    }
    return canonicalSource;
  }, [record, resolved, canonicalSource]);

  const renderLanguage = source?.language;
  return {
    source,
    loading: recordsLoading || (!!record && resolving),
    // A resolvable render exists even while its URL is still being signed —
    // the mode gate must not flicker closed for the duration of the fetch.
    hasPublishedRender: !!canonicalSource || !!record,
    displayLanguage:
      renderLanguage && renderLanguage !== (originalLanguage || 'en')
        ? renderLanguage
        : null,
    sourceRecord: source && record ? record : null,
  };
}
